// 阅读完成率漏斗（横向柱状图，20 节点）
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { wechatTheme } from '@/lib/theme'

export interface FunnelDatum {
  range: string
  percent: number
  count: number
}

interface Props {
  data: FunnelDatum[]
  height?: number
  className?: string
}

function colorForPercent(p: number): string {
  if (p >= 60) return wechatTheme.status.success
  if (p >= 30) return wechatTheme.status.warning
  return wechatTheme.status.error
}

export default function FunnelChart({ data, height, className }: Props) {
  const option = useMemo<EChartsOption>(() => {
    const reversed = [...data].reverse() // ECharts 横向 bar 从下到上画，反转让首个 range 在顶
    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: wechatTheme.bg.card,
        borderColor: wechatTheme.border.DEFAULT,
        textStyle: { color: wechatTheme.text.primary, fontSize: 12 },
        formatter: (p: any) => {
          const d = p.data
          return `<div style="font-weight:600">${d.range}</div>
            <div>完成率: <b>${d.percent.toFixed(1)}%</b></div>
            <div>人数: <b>${d.count.toLocaleString('zh-CN')}</b></div>`
        },
      },
      grid: { left: 70, right: 80, top: 12, bottom: 12 },
      xAxis: {
        type: 'value',
        show: false,
        max: 100,
      },
      yAxis: {
        type: 'category',
        data: reversed.map((d) => d.range),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: wechatTheme.text.tertiary, fontSize: 11 },
        inverse: false,
      },
      series: [
        {
          type: 'bar',
          data: reversed.map((d) => ({
            value: d.percent,
            range: d.range,
            percent: d.percent,
            count: d.count,
            itemStyle: { 
              color: colorForPercent(d.percent), 
              borderRadius: [0, 4, 4, 0],
              opacity: 0.9,
            },
          })),
          barWidth: 20,
          label: {
            show: true,
            position: 'right',
            color: wechatTheme.text.primary,
            fontSize: 12,
            fontWeight: 600,
            formatter: (p: any) => `${p.data.percent.toFixed(1)}% · ${p.data.count.toLocaleString('zh-CN')}`,
          },
          emphasis: {
            itemStyle: { opacity: 1, borderRadius: [0, 6, 6, 0] },
            label: { fontSize: 13, fontWeight: 700 },
          },
        },
      ],
    }
  }, [data])

  const computedHeight = height ?? Math.max(280, data.length * 28 + 40)

  return (
    <div className={className}>
      <ReactECharts
        option={option}
        style={{ height: computedHeight, width: '100%' }}
        notMerge
        lazyUpdate
        opts={{ renderer: 'canvas' }}
      />
    </div>
  )
}
